import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TeranetService } from '@services/teranet/teranet.service';
import { Business, BusinessAddress } from '@models/businesses';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { BusinessState } from '@state/business';
import LogRocket from 'logrocket';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { BusinessAddressesState } from '@state/business/address';
import { Select } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { openClose } from '@core/animations/animations';
import { MatDialogRef } from '@angular/material/dialog';
import { TestHooks } from './change-password.hooks';
import { EncryptFunctions } from '@functions/encryption.service';
import { TeranetCredentials } from '@models/teranet';

interface AccountNameOption {
	accountName: string;
	description: string;
}

@UntilDestroy()
@Component({
	selector: 'app-teranet-change-password',
	templateUrl: './change-password.component.html',
	animations: [openClose()],
	standalone: false,
})
export class TeranetChangePasswordComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@Select(BusinessAddressesState.getAll) addresses$: Observable<
		BusinessAddress[]
	>;
	@SelectSnapshot(BusinessState.getBusiness) business: Business;
	@SelectSnapshot(SelectedMatter.id) saleId;

	public accountNameOptions: DropdownOption<AccountNameOption>[] = [];
	public form_accountName: string;
	public form_username: string;
	public form_password_old: string;
	public form_password_new: string;
	public loading = false;
	public error: string;
	public submitted = false;

	@Input() set accountName(_accountName: string) {
		if (_accountName) {
			this.form_accountName = _accountName;
		}
	}

	@Input() set teranetAccount(_teranetAccount: TeranetCredentials) {
		if (_teranetAccount) {
			this.form_username = _teranetAccount?.username;
		}
	}

	FORM_VALUES = {
		ACCOUNTNAME: 'account_name',
		USERNAME: 'username',
		PASSWORD: 'password',
	};

	constructor(
		private teranetService: TeranetService,
		private profileDispatch: UserProfileDispatch,
		private dialogRef: MatDialogRef<TeranetChangePasswordComponent>,
		private encryptFunctions: EncryptFunctions,
	) {}

	ngOnDestroy(): void {
		this.profileDispatch.SetTeranetPassword(null);
	}

	ngOnInit(): void {
		combineLatest([this.addresses$])
			.pipe(untilDestroyed(this))
			.subscribe(([addresses]) => {
				const options: DropdownOption<AccountNameOption>[] = [];
				options.push({
					name: this.form_accountName,
					value: { accountName: this.form_accountName, description: 'Default' },
				});

				const addressAccounts = addresses.filter(
					(x) => x.teranetAccountName?.length > 0,
				);

				addressAccounts?.forEach((address) => {
					if (address?.teranetAccountName?.length > 0) {
						options.push({
							name: address?.teranetAccountName,
							value: {
								accountName: address?.teranetAccountName,
								description: address?.officeName,
							},
						});
					}
				});

				this.accountNameOptions = options;
			});
	}

	cancel() {
		this.profileDispatch.SetTeranetPassword(null);
		this.dialogRef.close();
	}

	onSubmit = async (form: NgForm) => {
		this.error = null;
		this.submitted = true;

		if (form.invalid) {
			return;
		}

		this.loading = true;

		try {
			await firstValueFrom(
				this.teranetService.changePassword({
					account: this.form_accountName,
					username: this.form_username,
					password: this.form_password_old,
					new_password: this.form_password_new,
				}),
			);

			const encryptedPassword = await firstValueFrom(
				this.encryptFunctions.encrypt(this.form_password_new),
			);

			await firstValueFrom(
				this.teranetService.upsertUserToTeranetAccount({
					account: this.form_accountName,
					username: this.form_username,
					password: encryptedPassword,
					userId: this.loggedInUser.uid,
				}),
			);

			this.profileDispatch.SetTeranetAccountName(this.form_accountName);
			this.profileDispatch.SetTeranetUsername(this.form_username);
			this.profileDispatch.SetTeranetPassword(this.form_password_new);
			this.profileDispatch.SetTeranetPasswordExpired(false);
			this.dialogRef.close();
		} catch (err) {
			if (err.details) {
				LogRocket.error('Error resetting teranet password', err.details);
				this.error = err.details?.error ?? 'Unable to reset teranet password';
			} else {
				const friendlyError = this.teranetService.HandleError(
					'Unable to change Teranet password',
					err,
				);
				this.error = friendlyError;
				LogRocket.error('Unable to change Teranet password', err);
			}
		} finally {
			this.loading = false;
		}
	};

	onAccountNameSelected(
		event: DropdownResult<DropdownOption<AccountNameOption>>,
	) {
		this.form_accountName = event.result.value.accountName;
	}
}
